/**
 * Tickets Page
 * 
 * Support ticket management for users.
 */
import { useState, useRef } from 'react';
import {
  MessageSquarePlus,
  MessageCircle,
  Clock,
  Search,
  Filter,
  ChevronRight,
  Send,
  AlertCircle,
  CheckCircle2,
  Loader2,
  LifeBuoy,
  ArrowLeft,
  Paperclip,
  X,
  FileText,
  Image as ImageIcon,
  File,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  useMyTickets,
  useTicket,
  useCreateTicket,
  useAddTicketMessage,
} from '@/hooks/use-support';
import type {
  TicketStatus,
  TicketCategory,
  TicketPriority,
} from '@/types/support';
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_COLORS,
  TICKET_PRIORITY_LABELS,
  TICKET_PRIORITY_COLORS,
  TICKET_CATEGORY_LABELS,
} from '@/types/support';
import { formatBrasilia } from '@/lib/brasiliaDate';

// Allowed file types
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;

// Helper to get file icon
const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon className="h-4 w-4" />;
  if (type.includes('pdf')) return <FileText className="h-4 w-4 text-red-500" />;
  return <File className="h-4 w-4" />;
};

// Format file size
const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default function TicketsPage() {
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  
  // File upload states
  const [ticketAttachments, setTicketAttachments] = useState<File[]>([]);
  const [messageAttachments, setMessageAttachments] = useState<File[]>([]);
  const ticketFileInputRef = useRef<HTMLInputElement>(null);
  const messageFileInputRef = useRef<HTMLInputElement>(null);
  
  // Form state for creating ticket
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'general' as TicketCategory,
    priority: 'medium' as TicketPriority,
  });
  
  // Queries
  const { data: ticketsData, isLoading: isLoadingTickets } = useMyTickets(
    statusFilter === 'all' ? undefined : statusFilter
  );
  const { data: selectedTicket } = useTicket(selectedTicketId || '');
  
  // Mutations
  const createTicket = useCreateTicket();
  const addMessage = useAddTicketMessage(selectedTicketId || '');
  
  // Filter tickets by search
  const filteredTickets = ticketsData?.tickets.filter(ticket =>
    ticket.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
    ticket.ticket_number.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];
  
  // Handle file selection
  const handleFileSelect = (
    files: FileList | null,
    setAttachments: React.Dispatch<React.SetStateAction<File[]>>,
    currentAttachments: File[]
  ) => {
    if (!files) return;
    
    const validFiles: File[] = [];
    const errors: string[] = [];
    
    Array.from(files).forEach(file => {
      if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Tipo de arquivo não permitido`);
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: Arquivo muito grande (máx. 10MB)`);
        return;
      }
      if (currentAttachments.length + validFiles.length >= MAX_FILES) {
        errors.push(`Máximo de ${MAX_FILES} arquivos permitidos`);
        return;
      }
      validFiles.push(file);
    });
    
    if (errors.length > 0) {
      alert(errors[0]);
    }
    
    if (validFiles.length > 0) {
      setAttachments(prev => [...prev, ...validFiles]);
    }
  };
  
  // Remove attachment
  const removeAttachment = (
    index: number,
    setAttachments: React.Dispatch<React.SetStateAction<File[]>>
  ) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };
  
  const handleCreateTicket = () => {
    if (!formData.subject.trim() || !formData.description.trim()) return;
    
    createTicket.mutate(
      { ...formData, attachments: ticketAttachments },
      {
        onSuccess: () => {
          setIsCreateDialogOpen(false);
          setFormData({
            subject: '',
            description: '',
            category: 'general',
            priority: 'medium',
          });
          setTicketAttachments([]);
        },
      }
    );
  };
  
  const handleSendMessage = () => {
    if ((!newMessage.trim() && messageAttachments.length === 0) || !selectedTicketId) return;
    
    addMessage.mutate(
      { message: newMessage || '[Anexo]', attachments: messageAttachments },
      {
        onSuccess: () => {
          setNewMessage('');
          setMessageAttachments([]);
        },
      }
    );
  };
  
  const getStatusIcon = (status: TicketStatus) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-4 w-4 text-blue-500" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-amber-500" />;
      case 'waiting_customer':
        return <MessageCircle className="h-4 w-4 text-purple-500" />;
      case 'resolved':
      case 'closed':
        return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
      default:
        return null;
    }
  };
  
  // Ticket Detail View
  if (selectedTicketId && selectedTicket) {
    return (
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-4 p-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSelectedTicketId(null)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold">{selectedTicket.subject}</h1>
                <Badge className={cn('text-xs', TICKET_STATUS_COLORS[selectedTicket.status])}>
                  {TICKET_STATUS_LABELS[selectedTicket.status]}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                #{selectedTicket.ticket_number} • Criado em {formatBrasilia(selectedTicket.created_at, "dd 'de' MMMM 'às' HH:mm")}
              </p>
            </div>
            <Badge variant="outline" className={TICKET_PRIORITY_COLORS[selectedTicket.priority]}>
              {TICKET_PRIORITY_LABELS[selectedTicket.priority]}
            </Badge>
          </div>
        </div>
        
        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="space-y-4 max-w-3xl mx-auto">
            {/* Original description */}
            <div className="flex gap-3">
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {selectedTicket.user_name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium">Você</span>
                  <span className="text-xs text-muted-foreground">
                    {formatBrasilia(selectedTicket.created_at, "dd/MM/yyyy 'às' HH:mm")}
                  </span>
                </div>
                <Card className="bg-muted/50">
                  <CardContent className="p-3">
                    <p className="text-sm whitespace-pre-wrap">{selectedTicket.description}</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            
            {/* Messages */}
            {selectedTicket.messages?.map((message) => (
              <div key={message.id} className={cn('flex gap-3', message.is_from_admin && 'flex-row-reverse')}>
                <Avatar className="h-8 w-8">
                  <AvatarFallback className={cn(
                    'text-xs',
                    message.is_from_admin ? 'bg-emerald-500 text-white' : 'bg-primary text-primary-foreground'
                  )}>
                    {message.is_from_admin ? 'S' : message.user_name?.charAt(0)?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className={cn('flex-1', message.is_from_admin && 'flex flex-col items-end')}>
                  <div className={cn('flex items-center gap-2 mb-1', message.is_from_admin && 'flex-row-reverse')}>
                    <span className="text-sm font-medium">
                      {message.is_from_admin ? 'Suporte' : 'Você'}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatBrasilia(message.created_at, "dd/MM/yyyy 'às' HH:mm")}
                    </span>
                  </div>
                  <Card className={cn(
                    message.is_from_admin
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900'
                      : 'bg-muted/50'
                  )}>
                    <CardContent className="p-3 space-y-2">
                      {message.message !== '[Anexos do ticket]' && message.message !== '[Anexo]' && (
                        <p className="text-sm whitespace-pre-wrap">{message.message}</p>
                      )}
                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <div className="flex flex-wrap gap-2 pt-1">
                          {message.attachments.map((att) => (
                            <a
                              key={att.id}
                              href={`${import.meta.env.VITE_API_URL?.replace('/api/v1', '') || 'http://localhost:8001'}${att.file_url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 bg-background/80 hover:bg-background px-3 py-1.5 rounded-lg text-sm border transition-colors"
                            >
                              {(() => {
                                if (att.file_type?.startsWith('image/')) return (
                                    <ImageIcon className="h-4 w-4 text-blue-500" />
                                );
                                if (att.file_type?.includes('pdf')) return (
                                    <FileText className="h-4 w-4 text-red-500" />
                                );
                                return (
                                    <File className="h-4 w-4 text-gray-500" />
                                );
                              })()}
                              <span className="max-w-[150px] truncate">{att.filename}</span>
                              {!!att.file_size && (
                                <span className="text-xs text-muted-foreground">
                                  ({formatFileSize(att.file_size)})
                                </span>
                              )}
                            </a>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
        
        {/* Message Input */}
        {!['closed', 'resolved'].includes(selectedTicket.status) && (
          <div className="border-t p-4 bg-background">
            <div className="max-w-3xl mx-auto space-y-3">
              {/* Attachments Preview */}
              {messageAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {messageAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-muted px-3 py-1.5 rounded-full text-sm"
                    >
                      {getFileIcon(file.type)}
                      <span className="max-w-[150px] truncate">{file.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({formatFileSize(file.size)})
                      </span>
                      <button
                        onClick={() => removeAttachment(index, setMessageAttachments)}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Textarea
                    placeholder="Digite sua mensagem..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    className="min-h-[80px] resize-none pr-12"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.ctrlKey) {
                        handleSendMessage();
                      }
                    }}
                  />
                  {/* Attach button inside textarea */}
                  <button
                    type="button"
                    onClick={() => messageFileInputRef.current?.click()}
                    className="absolute right-3 bottom-3 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    title="Anexar arquivo"
                  >
                    <Paperclip className="h-5 w-5" />
                  </button>
                  <input
                    type="file"
                    ref={messageFileInputRef}
                    className="hidden"
                    multiple
                    accept={ALLOWED_FILE_TYPES.join(',')}
                    onChange={(e) => {
                      handleFileSelect(e.target.files, setMessageAttachments, messageAttachments);
                      e.target.value = '';
                    }}
                  />
                </div>
                <Button
                  onClick={handleSendMessage}
                  disabled={(!newMessage.trim() && messageAttachments.length === 0) || addMessage.isPending}
                  className="self-end"
                >
                  {addMessage.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center mt-2">
              Ctrl + Enter para enviar
            </p>
          </div>
        )}
        
        {['closed', 'resolved'].includes(selectedTicket.status) && (
          <div className="border-t p-4 bg-muted/30">
            <div className="max-w-3xl mx-auto">
              <Alert className="border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/20">
                <AlertDescription className="space-y-3">
                  <div>
                    <p className="font-medium text-foreground">Seu ticket foi finalizado. ✨</p>
                    <p className="mt-1 text-muted-foreground">
                      Esperamos ter ajudado com sua solicitação. Caso surja uma nova dúvida ou outro assunto, basta abrir um novo chamado.
                    </p>
                  </div>

                  <div className="flex items-start gap-2 rounded-md bg-background/80 px-3 py-2 text-amber-700 dark:text-amber-300">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    <p>Este ticket agora é apenas para leitura e não pode ser reaberto.</p>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Ticket List View
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-primary" />
            Suporte
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie seus tickets de suporte
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)} className="gap-2">
          <MessageSquarePlus className="h-4 w-4" />
          Novo Ticket
        </Button>
      </div>
      
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por assunto ou número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(value) => setStatusFilter(value as TicketStatus | 'all')}
            >
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrar por status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os status</SelectItem>
                <SelectItem value="open">Abertos</SelectItem>
                <SelectItem value="in_progress">Em Andamento</SelectItem>
                <SelectItem value="waiting_customer">Aguardando Resposta</SelectItem>
                <SelectItem value="resolved">Resolvidos</SelectItem>
                <SelectItem value="closed">Fechados</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {/* Tickets List */}
      {(() => {
        if (isLoadingTickets) return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
        if (filteredTickets.length === 0) return (
            <Card>
              <CardContent className="py-12 text-center">
                <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum ticket encontrado</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all'
                    ? 'Tente ajustar os filtros de busca.'
                    : 'Você ainda não abriu nenhum ticket de suporte.'}
                </p>
                {!searchTerm && statusFilter === 'all' && (
                  <Button onClick={() => setIsCreateDialogOpen(true)}>
                    Abrir meu primeiro ticket
                  </Button>
                )}
              </CardContent>
            </Card>
        );
        return (
            <div className="space-y-3">
              {filteredTickets.map((ticket) => (
                <Card
                  key={ticket.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => setSelectedTicketId(ticket.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 mt-1">
                        {getStatusIcon(ticket.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <h3 className="font-medium truncate">{ticket.subject}</h3>
                            <p className="text-sm text-muted-foreground">
                              #{ticket.ticket_number} • {TICKET_CATEGORY_LABELS[ticket.category]}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge className={cn('text-xs', TICKET_STATUS_COLORS[ticket.status])}>
                              {TICKET_STATUS_LABELS[ticket.status]}
                            </Badge>
                            {!ticket.is_read_by_user && (
                              <span className="h-2 w-2 rounded-full bg-primary" />
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatBrasilia(ticket.created_at, "dd/MM/yyyy")}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="h-3 w-3" />
                            {ticket.message_count} {ticket.message_count === 1 ? 'mensagem' : 'mensagens'}
                          </span>
                          {ticket.last_message_at && (
                            <span>
                              Última atividade: {formatBrasilia(ticket.last_message_at, "dd/MM 'às' HH:mm")}
                            </span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
        );
      })()}
      
      {/* Pagination info */}
      {ticketsData && ticketsData.total > 0 && (
        <div className="text-center text-sm text-muted-foreground">
          Exibindo {filteredTickets.length} de {ticketsData.total} tickets
        </div>
      )}
      
      {/* Create Ticket Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-primary" />
              Novo Ticket de Suporte
            </DialogTitle>
            <DialogDescription>
              Descreva sua dúvida ou problema. Nossa equipe responderá o mais breve possível.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="subject">Assunto *</Label>
              <Input
                id="subject"
                placeholder="Ex: Dúvida sobre planos"
                value={formData.subject}
                onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="category">Categoria</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value as TicketCategory })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TICKET_CATEGORY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="priority">Prioridade</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => setFormData({ ...formData, priority: value as TicketPriority })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TICKET_PRIORITY_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid gap-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                placeholder="Descreva detalhadamente sua dúvida ou problema..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="min-h-[120px]"
              />
              <p className="text-xs text-muted-foreground">
                Mínimo de 10 caracteres
              </p>
            </div>
            
            {/* Attachments */}
            <div className="grid gap-2">
              <Label>Anexos (opcional)</Label>
              {/* <button> nativo em vez de div com role="button": traz foco e
                  Enter/Espaço de graça. type="button" para não submeter o form.
                  O input de arquivo fica FORA — input dentro de button é HTML
                  inválido. */}
              <button
                type="button"
                className="w-full border-2 border-dashed rounded-lg p-4 text-center hover:bg-muted/50 transition-colors cursor-pointer"
                onClick={() => ticketFileInputRef.current?.click()}
              >
                <Paperclip className="h-6 w-6 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  Clique para anexar arquivos
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Imagens, PDFs, documentos (máx. 10MB cada, até 5 arquivos)
                </p>
              </button>
              <input
                type="file"
                ref={ticketFileInputRef}
                className="hidden"
                multiple
                accept={ALLOWED_FILE_TYPES.join(',')}
                onChange={(e) => {
                  handleFileSelect(e.target.files, setTicketAttachments, ticketAttachments);
                  e.target.value = '';
                }}
              />
              
              {/* Attachments list */}
              {ticketAttachments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {ticketAttachments.map((file, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between bg-muted px-3 py-2 rounded-lg"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {getFileIcon(file.type)}
                        <span className="text-sm truncate">{file.name}</span>
                        <span className="text-xs text-muted-foreground flex-shrink-0">
                          ({formatFileSize(file.size)})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeAttachment(index, setTicketAttachments);
                        }}
                        className="text-muted-foreground hover:text-destructive p-1"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateTicket}
              disabled={
                createTicket.isPending ||
                formData.subject.length < 5 ||
                formData.description.length < 10
              }
            >
              {createTicket.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Criar Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
